import { getToken } from "../../services/api";
import { TelemetryClient, type TelemetryConfig } from "./client";
import { mapPathToScreen } from "./screens";
import { resetSession } from "./session";
import { clearTelemetryTokenCache } from "./token";

/**
 * usage-event-pipeline (emmi-observability task 4.9): the usage-telemetry
 * client for emmi-back-office. Disabled by default:
 * REACT_APP_TELEMETRY_INGEST_URL is empty until services/event-ingest is
 * deployed, so every call below is a true no-op until an operator sets it.
 */
let client: TelemetryClient | null = null;

function buildConfig(): TelemetryConfig {
  return {
    apiUrl: process.env.REACT_APP_API_URL ?? "",
    ingestUrl: process.env.REACT_APP_TELEMETRY_INGEST_URL ?? "",
    getAppAuthToken: () => getToken(),
    appKey: "emmi-back-office",
    platform: "web",
    appVersion: "dev",
    locale: "es-MX",
  };
}

export function getTelemetryClient(): TelemetryClient {
  if (!client) {
    client = new TelemetryClient(buildConfig());
    client.start();
  }
  return client;
}

export function trackAppOpened(): void {
  getTelemetryClient().track("app_opened", { cold_start: true });
}

export function trackScreenViewed(pathname: string): void {
  const screen = mapPathToScreen(pathname);
  getTelemetryClient().track("screen_viewed", {}, screen ? { screen } : {});
}

/**
 * usage-event-pipeline correction batch 4B item 1: wired into the app's own
 * logout path (`clearTokens()` in `services/api/index.ts`, the single point
 * useAuth's `logout()` clears the access/refresh tokens) so a telemetry
 * token minted for one user is never handed to whoever uses this device
 * next. Clears the in-memory telemetry token cache and rotates session_id
 * (anonymous_id is a device id and is intentionally left alone).
 *
 * Batch 4B final correction: also drops the singleton client's queued
 * events (never send a logged-out user's pending events under the next
 * identity) and resets its identify() state, so the next person's first
 * flush re-identifies instead of reusing the previous pseudonym.
 */
export function resetTelemetryIdentity(): void {
  clearTelemetryTokenCache();
  resetSession();
  client?.resetIdentity();
}
